import { Box, FormField, InputSearch } from '@domain-group/suit-theme'
import { matchFilterString } from '@domain-group/javascript-utils'
import { useDebounce, useSetState } from '@domain-group/react-hooks'

import ScreenContent from '@modals/NewOrderModal/components/ScreenContent'
import ScreenHeader from '@modals/NewOrderModal/components/ScreenHeader'

import type { UseNewOrderPayload } from '@modals/NewOrderModal'

import ServiceIcon from '@components/Icons/ServiceIcon'

import FavouriteOrder from './components/FavouriteOrder'

import type { BuildFavouriteOrderPayload } from '../../functions/buildFavouriteOrder'

import type { SetState } from './components/FavouriteOrder'

type BuildAndCreateOrderParams = ReturnType<UseNewOrderPayload['callbacks']['buildFavouriteOrder']> & {
  callbacks: {
    createCampaignQuoteItem: UseNewOrderPayload['callbacks']['createCampaignQuoteItem'],
    createCampaignQuoteTemplateItem: UseNewOrderPayload['callbacks']['createCampaignQuoteTemplateItem'],
    createOrderFromFavourite: UseNewOrderPayload['callbacks']['createOrderFromFavourite'],
  },
  itemType: 'quoteItem' | 'quoteTemplateItem',
  payload: BuildFavouriteOrderPayload['payload'],
}

const buildAndCreateOrder = (params: BuildAndCreateOrderParams) => {
  const {
    callbacks: {
      createCampaignQuoteItem,
      createCampaignQuoteTemplateItem,
      createOrderFromFavourite,
    },
    itemType,
    payload,
  } = params

  if (itemType === 'quoteItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteItem(payload)
  }

  if (itemType === 'quoteTemplateItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteTemplateItem(payload)
  }

  return createOrderFromFavourite(payload)
}

const defaultState = {
  filterString: '',
}

interface FavouriteOrderFlowProps {
  callbacks: {
    addItem: VoidFunction,
    closeModal: VoidFunction,
  },
  newOrderPayload: UseNewOrderPayload,
}

const FavouriteOrderFlow = (props: FavouriteOrderFlowProps) => {
  const {
    callbacks: {
      addItem,
      closeModal,
    },
    newOrderPayload: {
      callbacks: {
        buildFavouriteOrder,
        createCampaignQuoteItem,
        createCampaignQuoteTemplateItem,
        createOrderFromFavourite,
        resetState,
      },
      filteredFavouriteOrders,
    },
  } = props

  const [state, setState] = useSetState(defaultState)
  const { filterString } = state

  const debouncedString = useDebounce(filterString, 50)

  const filteredFavouriteOrdersByString = filteredFavouriteOrders.filter((favouriteOrder) => {
    const { title } = favouriteOrder

    const matchString = matchFilterString(title, debouncedString)
    return matchString
  })

  return (
    <>
      <ScreenHeader
        callbacks={{
          goBack: () => resetState(),
        }}
        imageComponent={(
          <Box
            alignItems="center"
            borderRadius={5}
            border="1px solid"
            borderColor="lineColor"
            height={50}
            justifyContent="center"
            overflow="hidden"
            width={84}
          >
            <ServiceIcon fillColor="#8C9FBF" height={38} serviceKey="favourites" width={38} />
          </Box>
        )}
      >
        <ScreenHeader.Title>
          Favourites
        </ScreenHeader.Title>

        <ScreenHeader.Subtitle>
          from All Providers
        </ScreenHeader.Subtitle>
      </ScreenHeader>

      <ScreenContent hasFooter={false}>
        <>
          <FormField label="Filter Favourites">
            {/*
             // @ts-expect-error TS2741 */}
            <InputSearch
              // @ts-expect-error TS7006
              onChange={e => setState({ filterString: e.target.value })}
              value={filterString}
            />
          </FormField>

          {filteredFavouriteOrdersByString.map(favouriteOrder => (
            <FavouriteOrder
              callbacks={{
                selectFavouriteOrder: (setButtonState: SetState) => {
                  const addItemPayload = {
                    callbacks: {
                      action: (
                        actionParams: ReturnType<UseNewOrderPayload['callbacks']['buildFavouriteOrder']>,
                      ) => (
                        // @ts-expect-error TS2345
                        buildAndCreateOrder({
                          ...actionParams,
                          callbacks: {
                            createCampaignQuoteItem,
                            createCampaignQuoteTemplateItem,
                            createOrderFromFavourite,
                          },
                        })
                      ),
                      afterAction: () => {
                        setButtonState({ loading: false })
                        return closeModal()
                      },
                      afterError: () => setButtonState({ loading: false }),
                      buildActionParams: () => {
                        const buildFavouriteOrderPayload = buildFavouriteOrder({
                          favouriteOrderId: favouriteOrder.id,
                        })

                        return [buildFavouriteOrderPayload]
                      },
                    },
                    toastText: 'Item Created',
                  }

                  // @ts-expect-error TS2554
                  addItem(addItemPayload)
                },
              }}
              key={favouriteOrder.id}
              favouriteOrder={favouriteOrder}
            />
          ))}
        </>
      </ScreenContent>
    </>
  )
}

export default FavouriteOrderFlow
